// RfqLineItemsEditor — inline-editable parts table for the Draft Review.
//
// Design principles applied (per Pricing-Sourcing-Unification PRD §3):
//   - Progressive disclosure: each row is COLLAPSED by default (PN + small
//     summary line); the edit pencil expands the row into editable inputs.
//     Only ONE row is expanded at a time so the panel stays calm.
//   - Delicate hovers: very light hover backgrounds — the surface stays
//     quiet at rest and announces interactivity only on hover.
//   - Inline editing, no modals: expansion happens in place.
//
// Phase 1.1 — edit qty / target / remove
// Phase 1.2 — edit per-line notes
//
// Edits live in local React state. Parent owns the state via `value` /
// `onChange`. `toLineItemOverrides()` converts state → API payload.

import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as RemoveIcon,
  UndoOutlined as UndoIcon,
  EditOutlined as EditIcon,
  CheckOutlined as DoneIcon,
  NoteAddOutlined as NoteAddIcon,
} from '@mui/icons-material';
import type { RfqLineItem } from '../../services/buyerAgentService';

export interface RfqLineItemsEditorValue {
  /** Edits keyed by lineItemId. Empty/absent → use the line as-is. */
  overrides: Record<
    string,
    {
      requestedQuantity?: number;
      targetPrice?: number;
      targetCurrency?: string;
      /** Phase 1.2 — per-line note ("+ at 1000 too", "with zinc plating", …). */
      notes?: string;
    }
  >;
  /** lineItemIds the buyer has marked for removal. They stay visible (struck-
   *  through) in the UI so the buyer can undo, but are excluded from the
   *  rendered template + dropped from rfq.lineItems on send. */
  removedIds: string[];
}

const NOTES_MAX_LEN = 280;
const emptyValue: RfqLineItemsEditorValue = { overrides: {}, removedIds: [] };

export function emptyLineItemsEditorValue(): RfqLineItemsEditorValue {
  return { overrides: {}, removedIds: [] };
}

interface RfqLineItemsEditorProps {
  baseLineItems: RfqLineItem[];
  value: RfqLineItemsEditorValue;
  onChange: (next: RfqLineItemsEditorValue) => void;
  defaultCurrency?: string;
  /** Inside an edit panel that already names the section: no header of its own. */
  embedded?: boolean;
}

const RfqLineItemsEditor: React.FC<RfqLineItemsEditorProps> = ({
  baseLineItems,
  value = emptyValue,
  onChange,
  defaultCurrency = 'USD',
  embedded = false,
}) => {
  const theme = useTheme();
  // Only one row may be expanded at a time — keeps the surface focused.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Per-line note is hidden by default (keeps the row calm); revealed by
  // the "+ Add note" affordance, or automatically when the line already
  // has a note. One open note input at a time, scoped per row.
  const [showNoteFor, setShowNoteFor] = useState<string | null>(null);

  const removedSet = useMemo(() => new Set(value.removedIds), [value.removedIds]);
  const activeCount = baseLineItems.filter((li) => !removedSet.has(li.lineItemId)).length;
  const removedCount = removedSet.size;

  const updateOverride = (
    lineItemId: string,
    patch: Partial<{
      requestedQuantity: number;
      targetPrice: number;
      targetCurrency: string;
      notes: string;
    }>,
  ) => {
    const prev = value.overrides[lineItemId] || {};
    const next = { ...prev, ...patch };
    const cleaned = Object.fromEntries(
      Object.entries(next).filter(([, v]) => v !== undefined && v !== '' as any),
    ) as typeof next;
    onChange({
      ...value,
      overrides: { ...value.overrides, [lineItemId]: cleaned },
    });
  };

  const remove = (lineItemId: string) => {
    if (removedSet.has(lineItemId)) return;
    if (expandedId === lineItemId) setExpandedId(null);
    if (showNoteFor === lineItemId) setShowNoteFor(null);
    onChange({ ...value, removedIds: [...value.removedIds, lineItemId] });
  };

  /** Called when the buyer collapses an expanded row. Resets the "show note
   *  input" state for lines that ended up with no note, so the next
   *  expansion starts with the cleaner "+ Add note" affordance instead of
   *  an empty textarea lingering. */
  const collapseRow = (li: RfqLineItem) => {
    setExpandedId(null);
    const o = value.overrides[li.lineItemId];
    const effectiveNote = o?.notes !== undefined ? o.notes : (li as any).notes ?? '';
    if (!effectiveNote && showNoteFor === li.lineItemId) {
      setShowNoteFor(null);
    }
  };

  const restore = (lineItemId: string) => {
    onChange({
      ...value,
      removedIds: value.removedIds.filter((id) => id !== lineItemId),
    });
  };

  const effective = (li: RfqLineItem) => {
    const o = value.overrides[li.lineItemId] || {};
    return {
      qty: o.requestedQuantity ?? li.requestedQuantity,
      target: o.targetPrice ?? li.targetPrice,
      currency: o.targetCurrency ?? li.targetCurrency ?? defaultCurrency,
      notes: o.notes !== undefined ? o.notes : (li as any).notes ?? '',
    };
  };

  return (
    <Box>
      {/* Section header */}
      {!embedded && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.25 }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              letterSpacing: 0.7,
              textTransform: 'uppercase',
              color: theme.palette.text.primary,
              fontWeight: 800,
            }}
          >
            Parts requested ({activeCount}{removedCount > 0 ? ` · ${removedCount} removed` : ''})
          </Typography>
          <Box sx={{ fontSize: '0.72rem', color: theme.palette.text.disabled, fontStyle: 'italic' }}>
            + Add from BOM · coming next
          </Box>
        </Stack>
      )}

      {/* Table — each row collapsed by default, click ✎ to expand */}
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {baseLineItems.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontSize: '0.85rem' }}>
            No line items on this RFQ.
          </Box>
        ) : (
          baseLineItems.map((li, idx) => {
            const isRemoved = removedSet.has(li.lineItemId);
            const isExpanded = expandedId === li.lineItemId && !isRemoved;
            const eff = effective(li);
            const hasOverride =
              !!value.overrides[li.lineItemId] &&
              Object.keys(value.overrides[li.lineItemId]).length > 0;

            return (
              <Box
                key={li.lineItemId}
                sx={{
                  borderTop: idx > 0 ? `1px solid ${theme.palette.divider}` : 'none',
                  bgcolor: isRemoved
                    ? alpha(theme.palette.text.disabled, 0.025)
                    : isExpanded
                      ? alpha(theme.palette.primary.main, 0.015)
                      : 'transparent',
                  transition: 'background 160ms ease',
                  '&:hover': {
                    bgcolor: isRemoved
                      ? alpha(theme.palette.text.disabled, 0.035)
                      : isExpanded
                        ? alpha(theme.palette.primary.main, 0.02)
                        : alpha(theme.palette.text.primary, 0.012),
                  },
                }}
              >
                {/* Summary row — always visible */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.75,
                    py: 1.25,
                    opacity: isRemoved ? 0.6 : 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    {/* Headline: PN · description */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        textDecoration: isRemoved ? 'line-through' : 'none',
                        color: isRemoved ? 'text.disabled' : 'text.primary',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={li.description}
                    >
                      <Box component="span" sx={{ color: 'primary.dark', fontFamily: 'monospace' }}>
                        {li.partNumber || 'no-pn'}
                      </Box>
                      {li.description && (
                        <Box component="span" sx={{ fontWeight: 400, color: 'text.primary', ml: 0.75 }}>
                          · {li.description}
                        </Box>
                      )}
                    </Typography>

                    {/* Sub-line: qty · target · notes preview */}
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.25 }}
                    >
                      <SummaryChip
                        label={`${eff.qty} ${li.unit || 'EA'}`}
                        muted={isRemoved}
                        emphasized={hasOverride && value.overrides[li.lineItemId]?.requestedQuantity !== undefined}
                      />
                      <SummaryChip
                        label={
                          typeof eff.target === 'number'
                            ? `Target ${formatPrice(eff.target)} ${eff.currency}`
                            : 'No target'
                        }
                        muted={typeof eff.target !== 'number' || isRemoved}
                      />
                      {eff.notes && (
                        <SummaryChip
                          label={`Note: ${eff.notes.length > 40 ? `${eff.notes.slice(0, 40)}…` : eff.notes}`}
                          muted={isRemoved}
                          emphasized
                        />
                      )}
                    </Stack>
                  </Box>

                  {/* Right-aligned actions */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    {isRemoved ? (
                      <Tooltip title="Restore this line">
                        <IconButton
                          size="small"
                          onClick={() => restore(li.lineItemId)}
                          sx={{ color: 'primary.main' }}
                        >
                          <UndoIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title={isExpanded ? 'Done editing' : 'Edit line'}>
                          <IconButton
                            size="small"
                            onClick={() => isExpanded ? collapseRow(li) : setExpandedId(li.lineItemId)}
                            sx={{
                              color: isExpanded ? 'primary.main' : 'text.secondary',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            {isExpanded ? <DoneIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 15 }} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove from this RFQ (you can undo)">
                          <IconButton
                            size="small"
                            onClick={() => remove(li.lineItemId)}
                            sx={{
                              color: 'text.disabled',
                              '&:hover': { color: 'error.main' },
                            }}
                          >
                            <RemoveIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Expanded edit area — only renders when this row is the active one */}
                {isExpanded && (
                  <Box
                    sx={{
                      px: 1.75,
                      pb: 1.5,
                      pt: 0.25,
                      borderTop: `1px dashed ${theme.palette.divider}`,
                      ml: 0,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}
                    >
                      <FieldLabel label="Qty">
                        <TextField
                          size="small"
                          type="number"
                          value={eff.qty ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            if (v === undefined) {
                              const { requestedQuantity, ...rest } = value.overrides[li.lineItemId] || {};
                              void requestedQuantity;
                              onChange({
                                ...value,
                                overrides: { ...value.overrides, [li.lineItemId]: rest },
                              });
                              return;
                            }
                            if (!Number.isFinite(v) || v <= 0) return;
                            updateOverride(li.lineItemId, { requestedQuantity: v });
                          }}
                          inputProps={{ min: 1, step: 1, style: { textAlign: 'right' } }}
                          sx={{
                            width: 80,
                            '& .MuiInputBase-input': {
                              fontFamily: 'monospace',
                              fontVariantNumeric: 'tabular-nums',
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              py: 0.5,
                            },
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 0.5 }}
                        >
                          {li.unit || 'EA'}
                        </Typography>
                      </FieldLabel>

                      <FieldLabel label="Target">
                        <TextField
                          size="small"
                          type="number"
                          placeholder="—"
                          value={eff.target ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            if (v === undefined) {
                              const { targetPrice, ...rest } = value.overrides[li.lineItemId] || {};
                              void targetPrice;
                              onChange({
                                ...value,
                                overrides: { ...value.overrides, [li.lineItemId]: rest },
                              });
                              return;
                            }
                            if (!Number.isFinite(v) || v < 0) return;
                            updateOverride(li.lineItemId, { targetPrice: v });
                          }}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                          sx={{
                            width: 92,
                            '& .MuiInputBase-input': {
                              fontFamily: 'monospace',
                              fontVariantNumeric: 'tabular-nums',
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              py: 0.5,
                            },
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 0.5 }}
                        >
                          {eff.currency}/{li.unit || 'EA'}
                        </Typography>
                      </FieldLabel>
                    </Stack>

                    <Box sx={{ mt: 1.25 }}>
                      {!eff.notes && showNoteFor !== li.lineItemId ? (
                        // Quiet affordance shown when no note exists — keeps
                        // the expanded row uncluttered. Click reveals the
                        // textarea, autofocused.
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => setShowNoteFor(li.lineItemId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') setShowNoteFor(li.lineItemId);
                          }}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            color: 'text.secondary',
                            py: 0.25,
                            borderRadius: 1,
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          <NoteAddIcon sx={{ fontSize: 16 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.74rem' }}>
                            Add note
                          </Typography>
                        </Box>
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          autoFocus={showNoteFor === li.lineItemId}
                          placeholder='Note for this line (e.g. "+ at 1,000 too", "with zinc plating")'
                          value={eff.notes}
                          onChange={(e) => {
                            const v = e.target.value.slice(0, NOTES_MAX_LEN);
                            if (v === '') {
                              const { notes, ...rest } = value.overrides[li.lineItemId] || {};
                              void notes;
                              onChange({
                                ...value,
                                overrides: { ...value.overrides, [li.lineItemId]: rest },
                              });
                              return;
                            }
                            updateOverride(li.lineItemId, { notes: v });
                          }}
                          inputProps={{ maxLength: NOTES_MAX_LEN }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: '0.78rem',
                              py: 0.5,
                              lineHeight: 1.45,
                            },
                            '& .MuiInputBase-input::placeholder': {
                              fontSize: '0.74rem',
                              opacity: 0.7,
                              fontStyle: 'italic',
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>

      {removedCount > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            color: 'text.secondary',
            fontSize: '0.72rem',
            fontStyle: 'italic',
          }}
        >
          {removedCount} line{removedCount === 1 ? '' : 's'} will be removed from the RFQ on send. Click the undo arrow to restore.
        </Typography>
      )}
    </Box>
  );
};

/** Small inline label + input grouping in the expanded edit area. */
const FieldLabel: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <Typography
      variant="caption"
      sx={{
        fontSize: '0.65rem',
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        color: 'text.secondary',
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>
    {children}
  </Stack>
);

/** Tiny pill used in the collapsed-row summary line. */
const SummaryChip: React.FC<{ label: string; muted?: boolean; emphasized?: boolean }> = ({
  label,
  muted,
  emphasized,
}) => {
  const theme = useTheme();
  return (
    <Typography
      variant="caption"
      sx={{
        fontSize: '0.7rem',
        color: muted ? 'text.disabled' : emphasized ? theme.palette.primary.dark : 'text.secondary',
        fontWeight: emphasized ? 600 : 500,
        fontFamily: emphasized ? undefined : undefined,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  );
};

function formatPrice(v: number): string {
  if (v < 1) return `$${parseFloat(v.toPrecision(3))}`;
  return `$${v.toFixed(2)}`;
}

/**
 * Helper to convert the editor state into the `lineItemOverrides` array the
 * sendRfq endpoint expects. Returns only entries that actually carry a
 * change — clean payload, easier to audit.
 */
export function toLineItemOverrides(
  value: RfqLineItemsEditorValue,
  baseLineItems: RfqLineItem[],
): Array<{
  lineItemId: string;
  remove?: boolean;
  requestedQuantity?: number;
  targetPrice?: number;
  targetCurrency?: string;
  notes?: string;
}> {
  const removed = new Set(value.removedIds);
  const out: Array<{
    lineItemId: string;
    remove?: boolean;
    requestedQuantity?: number;
    targetPrice?: number;
    targetCurrency?: string;
    notes?: string;
  }> = [];

  for (const li of baseLineItems) {
    if (removed.has(li.lineItemId)) {
      out.push({ lineItemId: li.lineItemId, remove: true });
      continue;
    }
    const o = value.overrides[li.lineItemId];
    if (!o) continue;
    const entry: any = { lineItemId: li.lineItemId };
    let dirty = false;
    if (typeof o.requestedQuantity === 'number' && o.requestedQuantity !== li.requestedQuantity) {
      entry.requestedQuantity = o.requestedQuantity;
      dirty = true;
    }
    if (typeof o.targetPrice === 'number' && o.targetPrice !== li.targetPrice) {
      entry.targetPrice = o.targetPrice;
      dirty = true;
    }
    if (o.targetCurrency && o.targetCurrency !== li.targetCurrency) {
      entry.targetCurrency = o.targetCurrency;
      dirty = true;
    }
    if (typeof o.notes === 'string' && o.notes !== ((li as any).notes ?? '')) {
      entry.notes = o.notes;
      dirty = true;
    }
    if (dirty) out.push(entry);
  }
  return out;
}

export default RfqLineItemsEditor;
